import { NgClass, NgIf } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core'
import { FormControl, FormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { NgxScannerQrcodeComponent, ScannerQRCodeResult } from 'ngx-scanner-qrcode'
import { Observable } from 'rxjs'

import { BaseComponent, ListItem, MaterialTransfer } from '../../shared'
import { CameraService } from '../../shared/camera-service'

@Component({
    templateUrl: './material-transfer.component.html',
    styleUrls: ['./material-transfer.component.css'],
    standalone: true,
    imports: [FormsModule, NgClass, WjInputModule, NgIf, MatButton, FaIconComponent, NgxScannerQrcodeComponent],
})
export class MaterialTransferComponent extends BaseComponent implements OnDestroy, OnInit {
    @ViewChild('action') action!: NgxScannerQrcodeComponent

    private cameraService = inject(CameraService)

    myControl = new FormControl()
    faSpinner = faSpinner
    get isToolInstance(): boolean {
        if (this.tool) {
            return !this.tools.find(x => x.barcode == this.tool).isConsumable
        }
        return false
    }

    tool = ''
    toolOptions: ListItem<string>[] = []
    tools: any[] = []

    consumableSources: any[] = []
    source = ''
    sourceOptions: ListItem<string>[] = []
    sources: any[] = []

    consumableDestinations: any[] = []
    toolDestinations: any[] = []
    destination = ''
    destinationOptions: ListItem<string>[] = []
    destinations: any[] = []

    userList: ListItem<number>[] = []
    requestedByHrRef = undefined

    quantity = 1
    quantityAvailable = 0
    isSaving = false
    saved = false
    filteredOptions!: Observable<any[]>
    validationMessage = ''

    get isCollapsed(): boolean {
        if (this.tool && this.source && this.destination) return true
        return false
    }
    get availableQuantity(): number {
        if (this.source) {
            const match = this.tools.filter(x => x.equipment === this.source)[0]
            if (match) {
                return match?.available ?? 0
            }
        }
        return 0
    }
    config: any = {
        vibrate: 400,
        isBeep: false,
        constraints: {
            audio: false,
        },
    }

    constructor() {
        super()
        this.refresh()
    }

    async ngOnInit() {
        const cameras = await this.cameraService.getCameras()
        const hasEnvironmentCamera = cameras.some(camera => camera.facingMode === 'environment')
        if (this.config?.constraints?.video) {
            this.config.constraints.video = hasEnvironmentCamera
                ? { facingMode: { exact: 'environment' } }
                : { facingMode: { exact: 'user' } }
        }
    }

    ngOnDestroy() {
        if (this.action) this.action.stop()
    }

    refresh() {
        const params = new HttpParams().set('withJobs', true)
        const params2 = new HttpParams().set('withJobs', false)
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('api/VpMaterial/GetToolsMaterials'),
                    this.services.data.get('/api/VpMaterial/GetMaterialLocations', params2),
                    this.services.data.get('/api/VpMaterial/GetMaterialLocations', params),
                    this.services.data.get('/api/VpMaterial/GetSmallToolLocations'),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                )
                .subscribe({
                    next: result => {
                        this.tools = result[0]
                        this.toolOptions = this.tools.map(x => new ListItem(x.barcode, x.name))
                        this.sourceOptions = []
                        this.consumableSources = result[1]
                        this.consumableDestinations = result[2]
                        this.toolDestinations = result[3]

                        // Only show active users as options.
                        this.userList = result[4].map(x => new ListItem(x.id, x.name))

                        // Finally, start the camera.
                        this.action.start()
                        this.reset()
                        this.initialLoadingComplete()
                    },
                }),
        )
    }

    toolChanged() {
        this.validationMessage = ''

        // The 'destination' options will change depending on the type of tool/material
        // Tools will go to HQ or Trucks.
        // Consumable Materials will go to HQ, Conexes, or Jobs.
        const tool = this.tools.find(x => x.barcode == this.tool)
        if (!tool) {
            return
        }
        if (tool.isConsumable) {
            this.sourceOptions = this.consumableSources.map(x => new ListItem(x.key, x.value))
            this.destinationOptions = this.consumableDestinations.map(x => new ListItem(x.key, x.value))
        } else {
            this.destinationOptions = this.toolDestinations.map(x => new ListItem(x.key, x.value))
        }
    }

    getSourceQuantities() {
        if (!this.source || !this.tool) return

        const params = new HttpParams().set('material', this.tool).set('location', this.source)
        this.cancelableRequest(
            'sourceQuantities',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/MaterialTransfer/GetSourceQuantities', params)).subscribe({
                next: result => {
                    this.quantityAvailable = result[0]
                },
            }),
        )
    }

    onError(e: any): void {
        alert(e)
    }

    onScan(e: ScannerQRCodeResult[]) {
        if (!e[0]) {
            return
        }
        const val = e[0].value
        if (this.tools.filter(x => x.barcode === val).length > 0) {
            if (!this.tool) {
                this.tool = val
                this.services.notification.success('Tool/Material Entered', undefined, true)
            }
            return
        }
        if (this.sources.filter(x => x.equipment === val).length > 0) {
            if (!this.source) {
                this.source = val
                this.services.notification.success('Transfer Source Entered', undefined, true)
            }
            return
        }
        if (this.destinations.filter(x => x.equipment === val).length > 0) {
            if (!this.destination) {
                this.destination = val
                this.services.notification.success('Transfer Destination Entered', undefined, true)
            }
            return
        }
        if (this.tool && this.source && this.destination) {
            this.action.pause()
            return
        }
        this.services.notification.warning('QR Code Not Recognized', undefined, true)
    }

    reset() {
        this.tool = ''
        this.destination = ''
        this.source = ''
        this.requestedByHrRef = undefined
        this.quantity = 1
        this.action.start()
        this.action.play()
        this.saved = false
        this.validationMessage = ''
        setTimeout(() => {
            this.destination = ''
            this.source = ''
            this.requestedByHrRef = undefined
        }, 10)
    }

    save() {
        this.isSaving = true
        const entry = new MaterialTransfer()
        entry.material = this.tool
        entry.quantity = this.quantity ?? 1
        entry.location = this.destination
        entry.userHrRef = this.services.authentication.hrref
        entry.date = new Date()
        entry.isToolInstance = this.isToolInstance
        entry.sourceLocation = this.source
        entry.requestedByHrRef = this.requestedByHrRef

        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.materialTransfers.add(entry)).subscribe({
                next: () => {
                    this.saved = true
                    this.isSaving = false
                    this.action.stop()
                },
                error: () => {
                    this.isSaving = false
                    this.services.notification.error('There was a problem saving the transfer.')
                },
            }),
        )
    }

    displayFn(job: any): string {
        return job
    }
}
